import { ChatService } from "@/services";
import { CHAT_QUERY_KEYS, ChatMessageDto, CommonParams } from "@/types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";

export const useGetChatMessages = (
  chatId: number,
  params: CommonParams<{}>,
  onDataRefresh?: () => void // Callback parameter
) => {
  // Internal list messages
  const [messages, setMessages] = useState<ChatMessageDto[]>([]);

  // Implementation here
  const {
    data,
    fetchNextPage,
    hasNextPage,
    refetch,
    isLoading,
    isFetching,
    isRefetching,
  } = useInfiniteQuery({
    queryKey: [CHAT_QUERY_KEYS.CHAT_MESSAGES, chatId, params],
    queryFn: ({ pageParam }: { pageParam: number | undefined }) =>
      ChatService.getChatMessages(chatId, {
        ...params,
        limit: 24,
        page: pageParam ? undefined : 1, // only load page 1, then load by lastMessageId
        lastMessageId: pageParam,
      }),
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      const hasNextPage = lastPage.pagination?.hasNextPage;

      if (!hasNextPage) return undefined;

      return lastPage.data[lastPage.data.length - 1].id;
    },
    retry: 1,
    // staleTime: 5 * 60 * 1000, // Data fresh trong 5 phút
    // gcTime: 10 * 60 * 1000, // Cache 10 phút
    refetchOnMount: true, // Refetch khi component mount
    refetchOnWindowFocus: true, // Allow refetch khi focus window to prevent lost socket data
    // refetchOnReconnect: false, // Không refetch khi reconnect
  });

  // flat array of page to array of message
  useEffect(() => {
    setMessages(data?.pages.flatMap((page) => page.data) || []);
  }, [data]);

  // Call onDataRefresh callback when data is refetched
  useMemo(() => {
    if (isRefetching && onDataRefresh) {
      onDataRefresh();
    }
  }, [isRefetching, onDataRefresh]);

  // Function edit messages by id and new message data
  const editMessageById = useCallback(
    (messageId: number, newMessageData: Partial<ChatMessageDto>) => {
      setMessages((prevMessages) =>
        prevMessages.map((message) =>
          message.id === messageId ? { ...message, ...newMessageData } : message
        )
      );
    },
    []
  );

  return {
    messages,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetching,
    refetch,
    editMessageById,
  };
};
